import type { MemberConsentType } from './consents';
import { SONA_CONSENT_DOCUMENT_VERSION } from '../consents/sona-consent';

// ─── consent document registry ──────────────────────────────────────────────
//
// One entry per registration consent document: which text the member agreed
// to (`documentVersion`), which bundled PDF renders the evidence
// (`templateFile` + `templateVersion`), what the chart calls it
// (`displayName`), which sign-up control evidences it (`checkbox`), and the
// AcroForm field names the render fills. Adding a consent is a new entry plus a
// template — never a new table or column. `documentVersion` and
// `templateVersion` are deliberately separate: re-typesetting a template must
// never rewrite what an existing row says the member was shown.

/**
 * The two sign-up controls that evidence registration consents.
 *
 * `terms` is the policies control: Terms of Use, Privacy Policy and Notice of
 * Privacy Practices, plus the calls-and-texts agreement, a clause recorded as
 * `calls_and_texts` only when the client says its control showed it.
 * `care` is the care control: the Informed Consent for Telehealth Services and
 * the 42 CFR Part 2 release. On the wire the two controls are still the
 * `terms_consent` and `hipaa_consent` request fields and the `members`
 * timestamp columns of the same names — `hipaa_consent` evidences the `care`
 * control; the field name is historical and was deliberately not renamed.
 */
export type ConsentCheckbox = 'terms' | 'care';

export interface ConsentDocumentSpec {
  /** Version of the text the member is shown and agrees to. */
  readonly documentVersion: string;
  /** Version of the bundled PDF template that renders the evidence. */
  readonly templateVersion: string;
  /** File name under the render Lambda's bundled `templates/` directory. */
  readonly templateFile: string;
  /** The document's display name in the member's Healthie chart. */
  readonly displayName: string;
  /** Which sign-up control evidences this consent; null until it has one. */
  readonly checkbox: ConsentCheckbox | null;
  /**
   * Whether the render job may stamp this document today. `held` keeps the
   * row as the record but produces no PDF: the template is not the text the
   * member is shown yet (legal typesetting pending), and a stamped placeholder
   * would be evidence of nothing. Display names and document versions stay
   * live for a held entry; only rendering waits.
   */
  readonly render: 'enabled' | 'held';
  /** AcroForm text-field names the render fills; `signature` is always present. */
  readonly fields: {
    readonly signature: string;
    readonly signatureDate?: string;
    readonly patientName?: string;
  };
}

/** Consent types captured at registration, in display order. */
export const REGISTRATION_CONSENT_TYPES = [
  'terms_of_use',
  'privacy_policy',
  'notice_of_privacy_practices',
  'tcpa_authorization',
  'telehealth_informed',
  'part2_release',
] as const satisfies readonly MemberConsentType[];
export type RegistrationConsentType = (typeof REGISTRATION_CONSENT_TYPES)[number];

/** Every consent type that has a document template: the registration set plus the Sona AI consent. */
export const DOCUMENTED_CONSENT_TYPES = [
  ...REGISTRATION_CONSENT_TYPES,
  'ai_sona',
] as const satisfies readonly MemberConsentType[];
export type DocumentedConsentType = (typeof DOCUMENTED_CONSENT_TYPES)[number];

// Every entry renders. The registration set is Legal's November 2026 bundle
// (effective 2 November 2026), so every registration document carries the
// one documentVersion `2026-11-02`: the Privacy Policy, Notice of Privacy
// Practices, TCPA Authorization and Part 2 release are new text; the Terms
// of Use and the Informed Consent for Telehealth Services keep the text the
// member site already shows (Legal re-issued neither), typeset onto a
// template that carries the approved signature block. A row recorded at an
// earlier version (`2024-web`, `2026-04`, `2026-08`) keeps its record and is
// never stamped onto a template, because the render job only stamps a
// consent whose recorded version is the one served now. The earlier
// placeholder and Part 2 templates stay in the templates directory but
// nothing reads them. The Sona AI consent renders too — its text is approved
// and is what the screen shows, word for word (SONA_CONSENT_DOCUMENT).
export const CONSENT_DOCUMENTS: Readonly<Record<DocumentedConsentType, ConsentDocumentSpec>> = {
  terms_of_use: {
    // The member-site text (the web app's terms-of-use document), unchanged
    // by Legal's November 2026 bundle; the version names the bundle.
    documentVersion: '2026-11-02',
    templateVersion: '2026-11-02',
    templateFile: 'TermsOfUseTemplate2026-11-02.pdf',
    displayName: 'Terms of Use',
    checkbox: 'terms',
    render: 'enabled',
    fields: { signature: 'signature', signatureDate: 'signatureDate' },
  },
  privacy_policy: {
    documentVersion: '2026-11-02',
    templateVersion: '2026-11-02',
    templateFile: 'PrivacyPolicyTemplate2026-11-02.pdf',
    displayName: 'Privacy Policy',
    checkbox: 'terms',
    render: 'enabled',
    fields: { signature: 'signature', signatureDate: 'signatureDate' },
  },
  telehealth_informed: {
    // The Version 2 April 2026 text, unchanged by Legal's November 2026
    // bundle: the template's text matches the linked public PDF word for
    // word, plus the signature block. The version names the bundle.
    documentVersion: '2026-11-02',
    templateVersion: '2026-04',
    templateFile: 'InformedConsentForTelehealthServicesTemplateVersion2April2026.pdf',
    displayName: 'Informed Consent for Telehealth Services',
    checkbox: 'care',
    render: 'enabled',
    fields: { signature: 'signature', signatureDate: 'signatureDate' },
  },
  notice_of_privacy_practices: {
    documentVersion: '2026-11-02',
    templateVersion: '2026-11-02',
    templateFile: 'NoticeOfPrivacyPracticesTemplate2026-11-02.pdf',
    displayName: 'Notice of Privacy Practices',
    checkbox: 'terms',
    render: 'enabled',
    fields: { signature: 'signature', signatureDate: 'signatureDate' },
  },
  tcpa_authorization: {
    documentVersion: '2026-11-02',
    templateVersion: '2026-11-02',
    templateFile: 'TcpaAuthorizationTemplate2026-11-02.pdf',
    displayName: 'TCPA Authorization',
    // Legal's calls-and-texts document, ticked with the policies control. A
    // sign-up records it only when the client says that control linked it
    // (`tcpa_authorization_consent`): the control's earlier copy named three
    // documents, and a member who ticked that copy never saw this one.
    checkbox: 'terms',
    render: 'enabled',
    fields: { signature: 'signature', signatureDate: 'signatureDate' },
  },
  part2_release: {
    // Legal's November 2026 text (the Referrals row becomes Referrals /
    // Emergency Escalations and names crisis and emergency recipients), with
    // the patient-name blank and the name-and-date signature lines.
    documentVersion: '2026-11-02',
    templateVersion: '2026-11-02',
    templateFile: 'ConsentForDisclosureOfDataTemplate2026-11-02.pdf',
    displayName: 'Consent for Disclosure of Data',
    // A distinct consent under 42 CFR Part 2: it shares the care control with
    // the telehealth consent, never the policies control.
    checkbox: 'care',
    render: 'enabled',
    fields: { signature: 'signature', signatureDate: 'signatureDate', patientName: 'patientName' },
  },
  ai_sona: {
    documentVersion: SONA_CONSENT_DOCUMENT_VERSION,
    templateVersion: '2026-09-25',
    templateFile: 'ConsentToAISonaTemplate2026-09-25.pdf',
    displayName: 'Consent to AI (Sona)',
    // Given on its own screen after registration, never by a sign-up control.
    checkbox: null,
    render: 'enabled',
    fields: { signature: 'signature', signatureDate: 'signatureDate' },
  },
};

/**
 * The registered document whose text governs the calls-and-texts clause: the
 * TCPA Authorization since Legal's November 2026 bundle (the Terms of Use's
 * Communication Preferences section before it). A `calls_and_texts` row
 * records this document's served version and has no registry entry of its
 * own (ADR-126 Rule 14).
 */
export const CALLS_AND_TEXTS_GOVERNING_DOCUMENT: RegistrationConsentType = 'tcpa_authorization';

/**
 * Which registration bundle a client presented. A client whose policies
 * control linked the TCPA Authorization showed the November 2026 bundle
 * (`current`); one whose control named three documents showed the bundle
 * before it (`before_tcpa`), and the member agreed to that bundle's text. A
 * consent is recorded at the version of the text the member was shown, never
 * at the version served now: the render guard then leaves it unstamped and
 * the standing read asks for the current bundle again.
 */
export type RegistrationBundle = 'current' | 'before_tcpa';

/** The version each document was served at by the bundle before the TCPA Authorization. */
export const BEFORE_TCPA_DOCUMENT_VERSIONS: Readonly<
  Record<Exclude<RegistrationConsentType, 'tcpa_authorization'>, string>
> = {
  terms_of_use: '2024-web',
  privacy_policy: '2024-web',
  notice_of_privacy_practices: '2024-web',
  telehealth_informed: '2026-04',
  part2_release: '2026-08',
};

/** The document whose Communication Preferences section governed the clause before the TCPA Authorization. */
export const BEFORE_TCPA_CALLS_AND_TEXTS_GOVERNING_DOCUMENT =
  'terms_of_use' satisfies RegistrationConsentType;

/** The version a document is recorded at for the bundle presented; null when that bundle never showed it. */
export function registrationDocumentVersion(
  type: RegistrationConsentType,
  bundle: RegistrationBundle,
): string | null {
  if (bundle === 'current') return CONSENT_DOCUMENTS[type].documentVersion;
  return type === 'tcpa_authorization' ? null : BEFORE_TCPA_DOCUMENT_VERSIONS[type];
}

/** The governing document version a calls-and-texts row records for the bundle presented. */
export function callsAndTextsGoverningVersion(bundle: RegistrationBundle): string {
  return bundle === 'current'
    ? CONSENT_DOCUMENTS[CALLS_AND_TEXTS_GOVERNING_DOCUMENT].documentVersion
    : BEFORE_TCPA_DOCUMENT_VERSIONS[BEFORE_TCPA_CALLS_AND_TEXTS_GOVERNING_DOCUMENT];
}

/** The consent types a ticked sign-up control evidences, in registry order. */
export function consentTypesForCheckbox(checkbox: ConsentCheckbox): RegistrationConsentType[] {
  return REGISTRATION_CONSENT_TYPES.filter((t) => CONSENT_DOCUMENTS[t].checkbox === checkbox);
}

export function isRegistrationConsentType(
  type: MemberConsentType,
): type is RegistrationConsentType {
  return REGISTRATION_CONSENT_TYPES.some((t) => t === type);
}

export function isDocumentedConsentType(type: MemberConsentType): type is DocumentedConsentType {
  return DOCUMENTED_CONSENT_TYPES.some((t) => t === type);
}

/** The document spec for a consent type, or null for types with no document (parental). */
export function consentDocumentFor(type: MemberConsentType): ConsentDocumentSpec | null {
  return isDocumentedConsentType(type) ? CONSENT_DOCUMENTS[type] : null;
}

/**
 * The spec the render job may stamp today, or null when the type has no
 * document or its document is held. Labels and sign-up versions use
 * `consentDocumentFor`; only rendering goes through this gate.
 */
export function renderableConsentDocumentFor(type: MemberConsentType): ConsentDocumentSpec | null {
  const spec = consentDocumentFor(type);
  return spec && spec.render === 'enabled' ? spec : null;
}

/**
 * Whether a consent recorded at `recorded` may be stamped onto `spec`'s
 * template: only when the row names the exact document version the registry
 * serves now. A row recorded at an older version (or with no version) agreed
 * to text the current template does not carry, so stamping it would evidence
 * text the member was never shown.
 */
export function consentDocumentVersionMatches(
  spec: ConsentDocumentSpec,
  recorded: string | null,
): boolean {
  return recorded !== null && recorded === spec.documentVersion;
}

// ─── where a member reads each registration document ────────────────────────
//
// The sign-up controls link every document they evidence, so the member can
// read what they agree to before ticking. Five documents are published on the
// member site (signup.pelagohealth.com, the Galapagos web app's signup host)
// and are linked where they live: Legal's November 2026 bundle names the
// member-site Terms of Use for members, never the corporate site's. The Part 2
// release has no published home, so the blank document is served as a static
// asset of this web app: the web client links the relative path and the
// mobile client prefixes the environment's web origin. The public path carries
// the document version so a new version gets a new path and an old link keeps
// naming the text it named.

/** Where a registration document is read: a fixed external page, or an asset of the web app. */
export type ConsentDocumentLink =
  | { readonly kind: 'external'; readonly href: string }
  | { readonly kind: 'web-asset'; readonly path: string };

/** Web-app-relative path of the blank Part 2 release served for reading at sign-up. */
export const PART2_RELEASE_PUBLIC_PATH = '/legal/consent-for-disclosure-of-data-2026-11-02.pdf';

export const REGISTRATION_CONSENT_LINKS: Readonly<
  Record<RegistrationConsentType, ConsentDocumentLink>
> = {
  // The member-site Terms of Use and Privacy Policy, not the corporate
  // site's pages at pelagohealth.com/terms/ and /privacy/.
  terms_of_use: { kind: 'external', href: 'https://signup.pelagohealth.com/?terms_of_use' },
  privacy_policy: { kind: 'external', href: 'https://signup.pelagohealth.com/?privacy_policy' },
  // No page exists on pelagohealth.com; this is the page registration links.
  notice_of_privacy_practices: {
    kind: 'external',
    href: 'https://signup.pelagohealth.com/?notice_of_privacy_practices',
  },
  tcpa_authorization: {
    kind: 'external',
    href: 'https://signup.pelagohealth.com/tcpa-authorization/',
  },
  // The current "Telehealth Services & Member Rights" version, the same URL
  // Settings → Legal opens. The similar-looking pelagohealth.com/informed-consent/
  // page is an outdated copy — do not use it.
  telehealth_informed: {
    kind: 'external',
    href: 'https://signup.pelagohealth.com/InformedConsentforTelehealthServicesandMemberRights.pdf',
  },
  part2_release: { kind: 'web-asset', path: PART2_RELEASE_PUBLIC_PATH },
};

/**
 * The URL a client opens for a registration document. An external document is
 * its fixed href. A web-app asset is its relative path on the web client, or
 * `webBaseUrl` + path on a client that lives outside the web origin.
 */
export function consentDocumentHref(
  type: RegistrationConsentType,
  options: { readonly webBaseUrl?: string } = {},
): string {
  const link = REGISTRATION_CONSENT_LINKS[type];
  if (link.kind === 'external') return link.href;
  return `${options.webBaseUrl ?? ''}${link.path}`;
}

/**
 * One sign-up control's sentence: the catalog key of its tagged copy and the
 * document each inline tag links. The copy is server-authored; the tag names
 * are the stable seam between the sentence and the registry, so a translation
 * may reorder the links but never renames them.
 */
export interface RegistrationConsentStatement {
  readonly control: ConsentCheckbox;
  /** Key inside the `auth` namespace carrying the tagged sentence. */
  readonly copyKey: string;
  readonly links: Readonly<Record<string, RegistrationConsentType>>;
}

/** The two sign-up sentences, in display order. */
export const REGISTRATION_CONSENT_STATEMENTS: readonly RegistrationConsentStatement[] = [
  {
    control: 'terms',
    copyKey: 'login.consentPolicies',
    links: {
      terms: 'terms_of_use',
      privacy: 'privacy_policy',
      notice: 'notice_of_privacy_practices',
      tcpa: 'tcpa_authorization',
    },
  },
  {
    control: 'care',
    copyKey: 'login.consentCare',
    links: {
      informedConsent: 'telehealth_informed',
      disclosure: 'part2_release',
    },
  },
];
